import { parse, inject } from 'regexparam'
import qs from 'qs'
import isEqual from 'lodash/isEqual'



const parsePath = (path, route) => {
  let i=0, out={};
  let matches = route.pattern.exec(path);
  while (i < route.keys.length) {
    out[ route.keys[i] ] = matches[++i] || null;
  }
  return out;
}


const stringifyPath = (route, params, query, hash) => {

  // Shallow copy paramters
  params = Object.assign({}, params)


  // Separate path params from params
  const path_params = {}
  route.matcher.keys.forEach(key => {
    if( params.hasOwnProperty(key) ){
      path_params[key] = params[key]
      delete params[key]
    }
  })


  // Build the pathname
  const path_string = inject(route.path, path_params)

  const missing_required_parameters = parse(path_string).keys

  const query_string = qs.stringify(query)

  const hash_string = qs.stringify(hash)

  return {
    path: path_string + (query_string ? `?${query_string}` : '') + (hash_string ? `#${hash_string}` : ''),
    missing_required_parameters: missing_required_parameters,
  }
}




export default class Router {


  constructor({routes}){

    this.previous_route = null
    this.current_route = null

    this.routes = routes
	this.pre_navigation_handlers = []
    this.navigation_handlers = []
	this.disable_navigation = undefined

    // Iterate through all entries to build a router and utilities
    Object.entries(this.routes).forEach(([key, route]) => {

      // Builds the base matcher for parsing, inspecting, and injecting route information
      route.matcher = parse(route.path)

      route.name = key // Does not get modified
      route.key = key // Gets modified
      

      // Build the path's HREF using default parameters
      route.href = args => {

        var {params, query, hash, options} = args || {}

        // Instantiate options with defaults
        options = Object.assign({preserve: false}, options)


        // If preserve option is true, maintain current paramters and update them with new parameters
        if(options.preserve === true){
            const current_route = this.currentRoute()
            params = Object.assign({}, current_route.params, params)
            query  = Object.assign({}, current_route.query,  query)
            hash   = Object.assign({}, current_route.hash,   hash)
        } else if (route.defaults) {
            params = Object.assign({}, route.defaults.params, params)
            query  = Object.assign({}, route.defaults.query , query)
            hash   = Object.assign({}, route.defaults.hash  , hash)
        }


        // Generate the new path based on paramters
        const path_object = stringifyPath(route, params, query, hash)


        // If required paramters are missing throw an error
        if(path_object.missing_required_parameters.length > 0){
          throw new Error(`Missing parameters in generated href: { ${path_object.missing_required_parameters} }.`)
        }


        return path_object.path
      }


      route.useNavigation = (args) => {

        var {params, query, hash, options} = args || {}

        // Instantiate options with defaults
        options = Object.assign({preserve: false, replace: false}, options)

        const href = route.href({params, query, hash, options})

        const fn = options.replace === true ? 'replaceState' : 'pushState'

        const localNavigationHandler = () => {

          if (this.current_route == null) this.current_route = this.currentRoute(true)

          let blocked
          if (this.disable_navigation) {
            const dest_pathname = href.split('?')[0]
            const dest_params = parsePath(dest_pathname, route.matcher)
            const dest_query =  qs.parse(href.split('?')[1])
            const dest_hash =   qs.parse(href.split('#')[1])
			if (this.disable_navigation?.route && route.name !== this.current_route?.name) {
				blocked = true
			}
			if (this.disable_navigation?.params && !isEqual(dest_params, this.current_route?.params)) {
				blocked = true
			}
			if (this.disable_navigation?.query && !isEqual(dest_query, this.current_route?.query)) {
				blocked = true
			}
			if (this.disable_navigation?.hash && !isEqual(dest_hash, this.current_route?.hash)) {
				blocked = true
			}
		  }
          this.pre_navigation_handlers.forEach(fn => fn(this.current_route, route, null, blocked))

		  if (blocked) return console.warn(`Router: Navigation to ${route.name} disabled.`)

          this.previous_route = this.current_route

          window.history[fn]({ href: href}, route.title || '', href)
          
          this.current_route = this.currentRoute(true)

          this.navigation_handlers.forEach(fn => fn(this.current_route))
        }

        localNavigationHandler.href = href

        localNavigationHandler.key = key

        return localNavigationHandler
      }


      route.navigate = args => {
        route.useNavigation(args)()
      }

    })

    window.addEventListener('popstate', event => {

		this.pre_navigation_handlers.forEach(fn => fn(this.current_route, null, event?.state?.href, this.disable_navigation != null))

		// TODO: Block Back button when disabled
		// // Back button detection when disabling
		// if (this.disable_navigation && event?.state?.href === this.current_route?.previous_route?.href({params: this.current_route?.previous_route?.params, query: this.current_route?.previous_route?.query, hash: this.current_route?.previous_route?.hash})) {
		// 	console.warn(`Router: Back Navigation disabled.`)
		// 	// Disable Back button, ref: https://stackoverflow.com/a/34337617
		// 	const current_href = this.current_route.href({params: this.current_route?.params, query: this.current_route?.query, hash: this.current_route?.hash})
		// 	history.pushState({href: current_href}, this.current_route.title || '', current_href)
		// 	history.go(1)
		// 	return
		// }

        this.current_route = this.currentRoute(true)

        this.navigation_handlers.forEach(fn => fn(this.current_route, event?.state?.href))
    })
  }

  onNavigation(fn){

    this.navigation_handlers.push(fn)

    return () => {
      let i = this.navigation_handlers.length
      while(i--){
        if(this.navigation_handlers[i] === fn) this.navigation_handlers.splice(i, 1)
      }
    }

  }

  currentRoute(force){

    if(!force && this.current_route) return this.current_route
    
    const pathname = window.location.pathname

    const route = Object.values(this.routes).find(route => route.matcher.pattern.test(pathname))

    if(!route) return null

    const params = Object.assign({}, route?.defaults?.params, parsePath(pathname, route.matcher))
    const query =  Object.assign({}, route?.defaults?.query,  qs.parse((window.location.search || '').split('?')[1]))
    const hash =   Object.assign({}, route?.defaults?.hash,   qs.parse((window.location.hash || '').split('#')[1]))

    return Object.assign({previous_route: this.previous_route}, route, {params, query, hash})
  }


	// NB: Currently only differentiates by name, not if its the same route but with different ID(s)
	previousDifferentRoute(current_route) {

		const curr_route = current_route ?? this.current_route
		const prev_route = curr_route?.previous_route

		if (!curr_route || !prev_route || !prev_route?.name || !curr_route?.name) return null

		if (prev_route.name !== curr_route.name) return prev_route

		return this.previousDifferentRoute(prev_route)

	}

}